/**
 * Pricing Master List - SUPER_ADMIN only
 * Table view with pagination, Add/Edit modals
 *
 * @author      iBirds Services
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState, useCallback } from "react";
import {
    Button,
    Col,
    Container,
    Form,
    Modal,
    Row,
    Spinner,
    Stack,
    Table,
} from "react-bootstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import fuziElevatorsApi from "../../api/fuziElevatorsApi";
import { ROUTES } from "../../constants/webRouting";
import { getUserDetails, formatDateForView } from "../../utils/helper";
import ".././../assets/css/ListComponentsTable.css";
import PubSub from "pubsub-js";

/** Table display: map raw backend values to user-friendly labels */
const APPLICABLE_FIELD_LABELS = {
    drive_type: "Machine Type",
    door_type: "Door Type",
    cabin_finish: "Cabin Finish",
    mirror: "Mirror Option",
};

/** Applicable Field options - labels per client Excel format */
const APPLICABLE_FIELD_OPTIONS = [
    { value: "drive_type", label: "Machine Type" },
    { value: "door_type", label: "Door Type" },
    { value: "cabin_finish", label: "Cabin Finish" },
    { value: "mirror", label: "Mirror Option" },
];

/** Applicable Value options - dependent on Applicable Field */
const APPLICABLE_VALUE_OPTIONS = {
    drive_type: [
        { value: "Gearless", label: "Gearless" },
        { value: "Geared", label: "Geared" },
    ],
    door_type: [
        { value: "MS Auto Door", label: "MS Auto Door" },
        { value: "Manual Door", label: "Manual Door" },
    ],
    cabin_finish: [
        { value: "SS", label: "SS" },
        { value: "Mirror Finish", label: "Mirror Finish" },
        { value: "Hairline", label: "Hairline" },
    ],
    mirror: [
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
    ],
};

const CALCULATION_TYPE_OPTIONS = [
    { value: "FIXED", label: "FIXED" },
    { value: "PER_STOP", label: "PER_STOP" },
    { value: "PER_FLOOR", label: "PER_FLOOR" },
    { value: "PER_KG", label: "PER_KG" },
];

const INITIAL_FORM = {
    item_name: "",
    applicable_field: null,
    applicable_value: null,
    calculation_type: null,
    base_price: "",
    is_active: true,
};

const INITIAL_ERRORS = {};

const PricingMasterList = () => {
    const userInfo = getUserDetails();
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [includeInactive, setIncludeInactive] = useState(false);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState(INITIAL_ERRORS);
    const [submitting, setSubmitting] = useState(false);
    const [editId, setEditId] = useState(null);
    const [deleteRecord, setDeleteRecord] = useState(null);

    const getApplicableValueOptions = useCallback(() => {
        const fieldValue = formData.applicable_field?.value ?? formData.applicable_field;
        return APPLICABLE_VALUE_OPTIONS[fieldValue] || [];
    }, [formData.applicable_field]);

    const resolveApplicableValueOption = useCallback((fieldValue, rawValue) => {
        if (!fieldValue || !rawValue) return null;
        const options = APPLICABLE_VALUE_OPTIONS[fieldValue] || [];
        return options.find((o) => o.value === rawValue || o.label === rawValue) || { value: rawValue, label: rawValue };
    }, []);

    useEffect(() => {
        if (userInfo?.userrole !== "SUPER_ADMIN") {
            navigate(ROUTES.HOME);
            return;
        }
        fetchData();
    }, [page, limit, includeInactive]);

    const fetchData = async () => {
        setLoading(true);
        const result = await fuziElevatorsApi.fetchPricingMaster({
            page,
            limit,
            include_inactive: includeInactive ? "true" : "false",
        });
        setLoading(false);
        if (result?.errors) {
            PubSub.publish("RECORD_ERROR_TOAST", {
                title: "Error",
                message: Array.isArray(result.errors) ? result.errors.map((e) => e.msg || e.message).join(", ") : result.errors,
            });
            setData([]);
            return;
        }
        if (result?.data) {
            setData(result.data);
            setTotal(result.total ?? 0);
            setTotalPages(result.totalPages ?? 1);
        } else {
            setData([]);
        }
    };

    const handleAddClick = () => {
        setFormData(INITIAL_FORM);
        setErrors(INITIAL_ERRORS);
        setShowAddModal(true);
    };

    const handleEditClick = (row) => {
        setEditId(row.id);
        const fieldOpt = row.applicable_field
            ? APPLICABLE_FIELD_OPTIONS.find((o) => o.value === row.applicable_field) || { value: row.applicable_field, label: row.applicable_field }
            : null;
        const valueOpt = resolveApplicableValueOption(row.applicable_field, row.applicable_value);
        setFormData({
            item_name: row.item_name || "",
            applicable_field: fieldOpt,
            applicable_value: valueOpt,
            calculation_type: row.calculation_type
                ? CALCULATION_TYPE_OPTIONS.find((o) => o.value === row.calculation_type) || { value: row.calculation_type, label: row.calculation_type }
                : null,
            base_price: row.base_price != null ? String(row.base_price) : "",
            is_active: row.is_active !== false,
        });
        setErrors(INITIAL_ERRORS);
        setShowEditModal(true);
    };

    const handleDeleteClick = (row) => {
        setDeleteRecord(row);
        setShowDeleteModal(true);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!(formData.item_name || "").trim()) {
            newErrors.item_name = "Item name is required";
        }
        if (!formData.applicable_field) {
            newErrors.applicable_field = "Applicable field is required";
        }
        if (!formData.applicable_value) {
            newErrors.applicable_value = "Applicable value is required";
        }
        if (!formData.calculation_type) {
            newErrors.calculation_type = "Calculation type is required";
        }
        const bp = parseFloat(formData.base_price);
        if (formData.base_price === "" || formData.base_price === null || formData.base_price === undefined) {
            newErrors.base_price = "Base price is required";
        } else if (isNaN(bp) || bp <= 0) {
            newErrors.base_price = "Base price must be greater than 0";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const getPayload = () => ({
        item_name: (formData.item_name || "").trim(),
        applicable_field: formData.applicable_field?.value ?? formData.applicable_field ?? "",
        applicable_value: formData.applicable_value?.value ?? formData.applicable_value ?? "",
        calculation_type: formData.calculation_type?.value ?? formData.calculation_type ?? "",
        base_price: parseFloat(formData.base_price) || 0,
        is_active: formData.is_active,
    });

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setSubmitting(true);
        const payload = getPayload();
        const result = await fuziElevatorsApi.createPricingMaster(payload);
        setSubmitting(false);
        if (result?.errors) {
            const errMsg = Array.isArray(result.errors)
                ? result.errors.map((e) => e.msg || e.message).join(", ")
                : result.errors;
            setErrors({ submit: errMsg });
            PubSub.publish("RECORD_ERROR_TOAST", { title: "Error", message: errMsg });
            return;
        }
        if (result?.success) {
            setShowAddModal(false);
            fetchData();
            PubSub.publish("RECORD_SAVED_TOAST", { title: "Success", message: "Pricing rule created successfully" });
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setSubmitting(true);
        const payload = getPayload();
        const result = await fuziElevatorsApi.updatePricingMaster(editId, payload);
        setSubmitting(false);
        if (result?.errors) {
            const errMsg = Array.isArray(result.errors)
                ? result.errors.map((e) => e.msg || e.message).join(", ")
                : result.errors;
            setErrors({ submit: errMsg });
            PubSub.publish("RECORD_ERROR_TOAST", { title: "Error", message: errMsg });
            return;
        }
        if (result?.success) {
            setShowEditModal(false);
            setEditId(null);
            fetchData();
            PubSub.publish("RECORD_SAVED_TOAST", { title: "Success", message: "Pricing rule updated successfully" });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteRecord) return;
        setSubmitting(true);
        const result = await fuziElevatorsApi.deletePricingMaster(deleteRecord.id);
        setSubmitting(false);
        setShowDeleteModal(false);
        setDeleteRecord(null);
        if (result?.errors) {
            PubSub.publish("RECORD_ERROR_TOAST", {
                title: "Error",
                message: Array.isArray(result.errors) ? result.errors.map((e) => e.msg || e.message).join(", ") : result.errors,
            });
            return;
        }
        if (result?.success) {
            fetchData();
            PubSub.publish("RECORD_SAVED_TOAST", { title: "Success", message: "Pricing rule deactivated successfully" });
        }
    };

    const handleFormChange = (name, value) => {
        setFormData((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "applicable_field") {
                next.applicable_value = null;
            }
            return next;
        });
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
        if (errors.submit) setErrors((prev) => ({ ...prev, submit: null }));
    };

    const handleBasePriceChange = (e) => {
        const v = e.target.value;
        if (v === "" || /^\d*\.?\d*$/.test(v)) {
            setFormData((prev) => ({ ...prev, base_price: v }));
            if (errors.base_price) setErrors((prev) => ({ ...prev, base_price: null }));
            if (errors.submit) setErrors((prev) => ({ ...prev, submit: null }));
        }
    };

    if (userInfo?.userrole !== "SUPER_ADMIN") {
        return null;
    }

    const applicableValueOptions = getApplicableValueOptions();
    const isApplicableValueDisabled = !formData.applicable_field;

    const formFieldsContent = (
        <>
            <Form.Group className="mb-3">
                <Form.Label>Item Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                    type="text"
                    name="item_name"
                    value={formData.item_name}
                    onChange={(e) => handleFormChange("item_name", e.target.value)}
                    isInvalid={!!errors.item_name}
                    maxLength={150}
                />
                <Form.Control.Feedback type="invalid">{errors.item_name}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Applicable Field <span className="text-danger">*</span></Form.Label>
                <Select
                    options={APPLICABLE_FIELD_OPTIONS}
                    value={formData.applicable_field}
                    onChange={(opt) => handleFormChange("applicable_field", opt)}
                    classNamePrefix="react-select"
                    placeholder="Select applicable field"
                    isClearable
                />
                {errors.applicable_field && (
                    <div className="invalid-feedback d-block">{errors.applicable_field}</div>
                )}
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Applicable Value <span className="text-danger">*</span></Form.Label>
                <Select
                    options={applicableValueOptions}
                    value={formData.applicable_value}
                    onChange={(opt) => handleFormChange("applicable_value", opt)}
                    classNamePrefix="react-select"
                    placeholder={isApplicableValueDisabled ? "Select applicable field first" : "Select applicable value"}
                    isClearable
                    isDisabled={isApplicableValueDisabled}
                />
                {errors.applicable_value && (
                    <div className="invalid-feedback d-block">{errors.applicable_value}</div>
                )}
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Calculation Type <span className="text-danger">*</span></Form.Label>
                <Select
                    options={CALCULATION_TYPE_OPTIONS}
                    value={formData.calculation_type}
                    onChange={(opt) => handleFormChange("calculation_type", opt)}
                    classNamePrefix="react-select"
                    placeholder="Select calculation type"
                    isClearable
                />
                {errors.calculation_type && (
                    <div className="invalid-feedback d-block">{errors.calculation_type}</div>
                )}
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Base Price <span className="text-danger">*</span></Form.Label>
                <Form.Control
                    type="text"
                    inputMode="decimal"
                    name="base_price"
                    placeholder="0.00"
                    value={formData.base_price}
                    onChange={handleBasePriceChange}
                    isInvalid={!!errors.base_price}
                />
                <Form.Control.Feedback type="invalid">{errors.base_price}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Check
                    type="switch"
                    id="is_active"
                    label="Active"
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange("is_active", e.target.checked)}
                />
            </Form.Group>

            {errors.submit && (
                <div className="alert alert-danger py-2">{errors.submit}</div>
            )}
        </>
    );

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <h5 className="mb-0">Pricing Master</h5>
                </Col>
                <Col xs="auto">
                    <Stack direction="horizontal" gap={2}>
                        <Form.Check
                            type="switch"
                            id="includeInactive"
                            label="Include inactive"
                            checked={includeInactive}
                            onChange={(e) => {
                                setIncludeInactive(e.target.checked);
                                setPage(1);
                            }}
                        />
                        <Button size="sm" className="btn-create" onClick={handleAddClick}>
                            Add Pricing
                        </Button>
                    </Stack>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" /> <span className="ms-2">Loading...</span>
                </div>
            ) : (
                <>
                    <div className="table-scroll my-3">
                        <Table striped className="shadow rounded sm" responsive>
                            <thead className="table-light">
                                <tr>
                                    <th className="bg-custom text-white">Item Name</th>
                                    <th className="bg-custom text-white">Applicable Field</th>
                                    <th className="bg-custom text-white">Applicable Value</th>
                                    <th className="bg-custom text-white">Calculation Type</th>
                                    <th className="bg-custom text-white">Base Price</th>
                                    <th className="bg-custom text-white">Active</th>
                                    <th className="bg-custom text-white">Created</th>
                                    <th className="bg-custom text-white text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center text-muted py-4">
                                            No pricing rules found.
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.item_name}</td>
                                            <td>{APPLICABLE_FIELD_LABELS[row.applicable_field] ?? row.applicable_field ?? "-"}</td>
                                            <td>{row.applicable_value || "-"}</td>
                                            <td>{row.calculation_type}</td>
                                            <td>{row.base_price != null ? Number(row.base_price).toLocaleString() : "-"}</td>
                                            <td>
                                                <span className={`badge ${row.is_active ? "bg-success" : "bg-secondary"}`}>
                                                    {row.is_active ? "Yes" : "No"}
                                                </span>
                                            </td>
                                            <td>{row.createddate ? formatDateForView(row.createddate) : "-"}</td>
                                            <td className="text-center">
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    className="me-1"
                                                    onClick={() => handleEditClick(row)}
                                                >
                                                    <i className="fa-solid fa-pen"></i>
                                                </Button>
                                                {row.is_active && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => handleDeleteClick(row)}
                                                    >
                                                        <i className="fa-solid fa-trash"></i>
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-2">
                            <span className="text-muted">
                                Showing {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of {total}
                            </span>
                            <div className="d-flex gap-1">
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    Previous
                                </Button>
                                <span className="align-self-center px-2">
                                    Page {page} of {totalPages}
                                </span>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Add Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add Pricing Rule</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddSubmit}>
                    <Modal.Body>
                        {formFieldsContent}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <><Spinner animation="border" size="sm" className="me-1" />Creating...</> : "Create"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => { setShowEditModal(false); setEditId(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Pricing Rule</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditSubmit}>
                    <Modal.Body>
                        {formFieldsContent}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowEditModal(false); setEditId(null); }} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <><Spinner animation="border" size="sm" className="me-1" />Updating...</> : "Update"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirm Modal */}
            <Modal show={showDeleteModal} onHide={() => { setShowDeleteModal(false); setDeleteRecord(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Deactivate Pricing Rule</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to deactivate this pricing rule? It will be soft-deleted (is_active = false).
                    {deleteRecord && (
                        <div className="mt-2 p-2 bg-light rounded">
                            <strong>{deleteRecord.item_name}</strong> - {deleteRecord.applicable_field} / {deleteRecord.applicable_value}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setDeleteRecord(null); }} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm} disabled={submitting}>
                        {submitting ? <><Spinner animation="border" size="sm" className="me-1" />Deactivating...</> : "Deactivate"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default PricingMasterList;
